import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getMenuNewCount } from "JS/API";

const POLL_INTERVAL_MS = 120 * 1000;
const CUTOFF_MS = 48 * 60 * 60 * 1000;
const LAST_SEEN_KEY = "admin_menu_seen_v1";

const MENU_KEYS = [
  "member",
  "inquiry",
  "cs_review",
  "self_application",
  "agencies",
  "patent_design",
  "application_info",
  "review_self",
  "bandname",
  "possibility",
  "payments",
];

const emptyCounts = () =>
  MENU_KEYS.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});

const loadLastSeen = () => {
  try {
    const raw = localStorage.getItem(LAST_SEEN_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveLastSeen = (data) => {
  try {
    localStorage.setItem(LAST_SEEN_KEY, JSON.stringify(data));
  } catch {
    // ignore storage errors
  }
};

const NewCountContext = createContext({
  counts: emptyCounts(),
  lastSeen: {},
  refresh: () => {},
  markSeen: () => {},
  shouldShowBadge: () => false,
});

export const useNewCount = () => useContext(NewCountContext);

// 페이지 진입 시 특정 메뉴를 "읽음" 처리하는 헬퍼 훅
export const useMarkMenuSeen = (key) => {
  const { markSeen } = useNewCount();
  useEffect(() => {
    markSeen(key);
  }, [key, markSeen]);
};

export const NewCountProvider = ({ children }) => {
  const [counts, setCounts] = useState(emptyCounts());
  const [lastSeen, setLastSeen] = useState(loadLastSeen);
  const isFetchingRef = useRef(false);
  const intervalRef = useRef(null);

  const refresh = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const result = await getMenuNewCount();
      if (result && typeof result === "object" && result.data) {
        const data = result.data;
        setCounts((prev) => {
          const next = { ...prev };
          MENU_KEYS.forEach((k) => {
            next[k] = typeof data[k] === "number" ? data[k] : 0;
          });
          return next;
        });
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  const markSeen = useCallback((key) => {
    if (!MENU_KEYS.includes(key)) return;
    setLastSeen((prev) => {
      const next = { ...prev, [key]: Date.now() };
      saveLastSeen(next);
      return next;
    });
  }, []);

  const shouldShowBadge = useCallback(
    (key) => {
      const count = counts[key] || 0;
      if (count <= 0) return false;
      const seen = lastSeen[key];
      if (!seen) return true;
      return Date.now() - seen >= CUTOFF_MS;
    },
    [counts, lastSeen]
  );

  useEffect(() => {
    refresh();
    const startPolling = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => {
        if (document.visibilityState === "visible") refresh();
      }, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh();
        startPolling();
      } else {
        stopPolling();
      }
    };

    startPolling();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refresh]);

  return (
    <NewCountContext.Provider
      value={{ counts, lastSeen, refresh, markSeen, shouldShowBadge }}
    >
      {children}
    </NewCountContext.Provider>
  );
};

export default NewCountContext;
